import { useState } from 'react';
import { AlertTriangle, Link2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/Button';
import { useClientMap, useItflowSettings, useProjectMap, useSyncProjects } from './useItflow';
import type { SyncResult } from './types';

/**
 * Chronos projects and ITFlow projects, per mapped client.
 *
 * Sync is two-way and by name within one client. Where both sides changed
 * since the last pass it reports a conflict instead of picking a winner,
 * silently choosing one is how somebody's edit disappears without trace.
 */
export function ProjectMappingPanel() {
  const settings = useItflowSettings();
  const configured = Boolean(settings.data?.configured);
  const clients = useClientMap(configured);
  const [clientId, setClientId] = useState<number | null>(null);
  const [lastSync, setLastSync] = useState<SyncResult | null>(null);
  const projects = useProjectMap(clientId);
  const sync = useSyncProjects();

  if (!configured) {
    return (
      <p className="text-small text-text-secondary">Connect an ITFlow instance first.</p>
    );
  }

  const mapped = (clients.data?.links ?? []).map((link) => ({
    link,
    name:
      clients.data?.chronos.find((c) => c.id === link.client_id)?.name ??
      `Client #${link.client_id}`,
  }));

  if (mapped.length === 0) {
    return (
      <p className="max-w-prose text-small text-text-secondary">
        Map a client first: projects only sync inside a mapped client.
      </p>
    );
  }

  const data = projects.data;
  const linkedProjects = new Set((data?.links ?? []).map((l) => l.project_id));
  const linkedRemote = new Set((data?.links ?? []).map((l) => l.itflow_project_id));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-2">
          <label htmlFor="itflow-project-client" className="text-small font-medium text-text-secondary">
            Client
          </label>
          <select
            id="itflow-project-client"
            value={clientId ?? ''}
            onChange={(e) => {
              setClientId(e.target.value ? Number(e.target.value) : null);
              setLastSync(null);
            }}
            className="min-h-touch rounded border border-border bg-surface-1 px-3 text-body text-text outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
          >
            <option value="">Choose a mapped client…</option>
            {mapped.map(({ link, name }) => (
              <option key={link.id} value={link.client_id}>
                {name}
              </option>
            ))}
          </select>
        </div>

        {clientId !== null && (
          <Button
            onClick={() =>
              sync.mutate(clientId, { onSuccess: (result) => setLastSync(result) })
            }
            loading={sync.isPending}
          >
            <RefreshCw size={14} strokeWidth={1.5} aria-hidden />
            Sync now
          </Button>
        )}
      </div>

      {lastSync && lastSync.conflicts.length > 0 && (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface-2 p-3">
          <p className="flex items-center gap-2 text-small text-warning">
            <AlertTriangle size={14} strokeWidth={1.5} aria-hidden />
            Changed on both sides: left alone
          </p>
          <ul className="flex flex-col gap-1">
            {lastSync.conflicts.map((conflict) => (
              <li key={conflict} className="text-small text-text-secondary">
                {conflict}
              </li>
            ))}
          </ul>
        </div>
      )}

      {clientId !== null && projects.isLoading && (
        <div className="h-32 animate-pulse rounded-lg bg-surface-2" aria-hidden />
      )}

      {data && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <section className="flex flex-col gap-2">
            <h3 className="text-small font-medium text-text-secondary">In Chronos</h3>
            <ul className="flex flex-col gap-1">
              {data.chronos.map((project) => (
                <li
                  key={project.id}
                  className="flex items-center justify-between gap-2 rounded border border-border bg-surface-1 px-3 py-2"
                >
                  <span className="truncate text-small text-text">{project.name}</span>
                  {linkedProjects.has(project.id) && (
                    <Link2 size={12} strokeWidth={2} className="shrink-0 text-success" aria-label="Mapped" />
                  )}
                </li>
              ))}
              {data.chronos.length === 0 && (
                <li className="text-small text-text-tertiary">No projects.</li>
              )}
            </ul>
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-small font-medium text-text-secondary">In ITFlow</h3>
            <ul className="flex flex-col gap-1">
              {data.itflow.map((project) => (
                <li
                  key={project.itflow_project_id}
                  className="flex items-center justify-between gap-2 rounded border border-border bg-surface-1 px-3 py-2"
                >
                  <span className="truncate text-small text-text">{project.name}</span>
                  {linkedRemote.has(project.itflow_project_id) && (
                    <Link2 size={12} strokeWidth={2} className="shrink-0 text-success" aria-label="Mapped" />
                  )}
                </li>
              ))}
              {data.itflow.length === 0 && (
                <li className="text-small text-text-tertiary">No projects.</li>
              )}
            </ul>
          </section>
        </div>
      )}

      <p className="max-w-prose text-small text-text-tertiary">
        Sync creates the missing side in both directions and links matching names.
        Requires the projects endpoints installed on your ITFlow instance, see
        deploy/itflow/README.md.
      </p>
    </div>
  );
}
